import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import useTeam from "../../hooks/useTeam";
import useTeams from "../../hooks/useTeams";
import AnalyticsIcon from "../../Icons/AnalyticsIcon";
import BellIcon from "../../Icons/BellIcon";
import BrimbleText from "../../Icons/BrimbleText";
// import DashboardIcon from "../../Icons/DashboardIcon";
import GlobeIcon from "../../Icons/GlobeIcon";
import MenuIcon from "../../Icons/MenuIcon";
import ServerIcon from "../../Icons/ServerIcon";
import SettingsIcon from "../../Icons/SettingsIcon";
// import Drawer from "../Micro/Drawer";
import IconButton from "../Micro/IconButton";
// import NavDrawer from "../NavDrawer";
import UserMenu from "../UserMenu";
import config from "../../config";
import Shortcut from "../Shortcut";
import { ArrowUpRight, Plug } from "lucide-react";

type NavLink = {
  title: string;
  link: string | string[];
  icon: React.ComponentType<any>;
  requiresPaid: boolean;
};

export const NavLinks: NavLink[] = [
  {
    title: "projects",
    link: ["/", "/projects"],
    icon: ServerIcon,
    requiresPaid: false,
  },
  {
    title: "scaling",
    link: "/autoscaling",
    icon: ArrowUpRight,
    requiresPaid: true,
  },
  {
    title: "mcp",
    link: "/mcp-servers",
    requiresPaid: false,
    icon: Plug,
  },
  { title: "domains", link: "/domains", icon: GlobeIcon, requiresPaid: false },
  { title: "usage", link: "/usage", icon: AnalyticsIcon, requiresPaid: false },
  {
    title: "settings",
    link: "/settings",
    icon: SettingsIcon,
    requiresPaid: false,
  },
];

const NavBar = () => {
  const [drawer, setDrawer] = React.useState<boolean>(false);
  const router: any = useRouter();
  const { team } = useTeam(router.query.team);
  const { teams } = useTeams();

  const ulRef = React.useRef<HTMLUListElement>(null);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const activeRoute = ulRef.current?.querySelector(
      "li.active[aria-selected=true]",
    );
    if (activeRoute) {
      const { offsetLeft, offsetWidth } = activeRoute as HTMLLIElement;
      sliderRef.current?.setAttribute(
        "style",
        `width: ${offsetWidth}px; transform: translateX(${offsetLeft}px)`,
      );
    }
  }, [router.pathname]);

  const isLinkActive = (link: string | string[]): boolean => {
    const currentPath = router.pathname;

    if (typeof link === "string") {
      if (link === "/") {
        return currentPath === "/teams/[team]";
      }
      return currentPath.includes(link);
    }

    return link.some((path) => {
      if (path === "/") {
        return currentPath === "/teams/[team]";
      }
      return currentPath.includes(path);
    });
  };

  return (
    <>
      <nav className={"py-2"}>
        <div className="container mx-auto flex max-w-7xl items-center justify-between px-4 py-5 md:py-0">
          <IconButton
            className="btn-ghost drawer-button block text-primary hover:bg-transparent dark:text-white md:hidden"
            onClick={() => setDrawer(!drawer)}
          >
            <MenuIcon />
          </IconButton>
          <div className="flex items-center justify-center space-x-2">
            <Link href={`/`}>
              <a>
                <BrimbleText className="w-28 text-primary" />
              </a>
            </Link>
            <p className="flex items-center justify-center rounded-md bg-light p-2 text-[10px] font-extrabold text-primary">
              TEAM
            </p>
          </div>
          <div className="z-10 flex items-center space-x-2">
            <button
              className="relative cursor-pointer"
              data-featurebase-changelog
            >
              <BellIcon />
              <span
                id="fb-update-badge"
                className="absolute right-0 top-0 flex h-4 w-4 -translate-y-1/2 translate-x-1/2 transform items-center justify-center rounded-full bg-red-600 text-xs text-white"
              ></span>
            </button>
            <span className="hidden px-2 md:block">
              <Shortcut />
            </span>
            <UserMenu
              type="team"
              teams={teams}
              avatar={
                team?.avatar ||
                `${config.AVATAR_URL}/initials/svg?seed=${
                  team?.name || router.query.name
                }`
              }
              username={team?.name || router.query.name}
            />
          </div>
        </div>
        <div className="hidden border-b border-gray-light dark:border-theme-color-light md:block">
          <ul
            ref={ulRef}
            className="container relative mx-auto flex w-full max-w-7xl items-center space-x-5 px-4"
          >
            {NavLinks.map((navLink) => {
              const NavIcon = navLink.icon;
              const isActive = isLinkActive(navLink.link);

              // Determine which link to use for navigation
              const navigationLink =
                typeof navLink.link === "string"
                  ? `/teams/${router.query.team}${navLink.link}`
                  : `/teams/${router.query.team}${navLink.link[0]}`;

              return (
                <Link href={navigationLink} key={navLink.title}>
                  <a className="">
                    <li
                      role=""
                      aria-selected={isActive}
                      className={`btn flex w-fit items-center gap-2 rounded-none border-x-0 border-t-0 px-[2px] font-medium capitalize hover:border-primary hover:bg-transparent dark:border-white ${
                        isActive
                          ? "active border-0 text-primary dark:text-white"
                          : "border-b-0 text-gray-dark dark:text-theme-color-lighter"
                      } text-xs`}
                    >
                      <NavIcon /> {navLink.title}
                    </li>
                  </a>
                </Link>
              );
            })}
            <li className="flex-grow"></li>
            <Link href={"https://docs.brimble.io/"}>
              <a className="">
                <li
                  className={`btn-ghost btn gap-2 rounded-none border-x-0 border-t-0 px-[2px] font-light capitalize hover:bg-transparent ${
                    router.pathname === "/docs"
                      ? "border-b-4 border-primary text-primary"
                      : "text-gray-dark dark:text-theme-color-lighter"
                  } text-xs`}
                >
                  <AnalyticsIcon /> Docs
                </li>
              </a>
            </Link>
            <div
              ref={sliderRef}
              className="slider absolute bottom-0 left-0 !mx-0 h-1 w-0 transition-['left'] duration-300 ease-in-out"
            />
          </ul>
        </div>
      </nav>

      {/* <Drawer
        open={drawer}
        onClose={() => setDrawer(!drawer)}
        title={""}
        description={""}
      >
        <NavDrawer onClose={() => setDrawer(!drawer)} />
      </Drawer> */}
    </>
  );
};

export default NavBar;
